/**
 * What the district owes the world outside it.
 *
 * `DESIGN-GOVERNORS.md` §8 promotes the existing Government Budget from a
 * settings panel to *"the mayor's domestic policy"* and gives it seven new lines.
 * Three of them can exist in a district with no polity above it, because all
 * three are priced against quantities this district already measures:
 *
 * > | Tolls on transit freight | + (Charter) | Corridor traffic |
 * > | Polity grants | + | Governor's discretion |
 * > | **Tithe** | − | Governor-set, % of district tax revenue |
 * > | Power & water bills | − | Metered at polity prices |
 * > | War repair costs | − | Re-grading craters is paid construction |
 *
 * So: the bills, the tithe, and the grant that is a negative tithe. Tolls need
 * freight, and craters need a war.
 *
 * ## The rationing rule, and why it is the point of this class
 *
 * Five ledgers in this build now predate the mechanic that reads them —
 * `deliveredKwCycles`, `uptimeCycles`, `wagesPaid`, `worksSpending`, and this
 * file's own `powerBilled` — on the grounds that Law 5 wants the measurement to
 * predate the case. For two of §8's lines, this is the case. Charging a levy is
 * arithmetic; making it *matter* is the thing the pool has never done, and §8 says
 * exactly where it should land:
 *
 * > Raising it forces mayors to choose: cut UBI (families bleed, migrate), cut
 * > infrastructure (roads decay, commerce slows — **the existing death-spiral**) or
 * > grow the tax base.
 *
 * The rule is one sentence: **a commitment the district has made to somebody
 * outside it is paid from a cycle's receipts first, and the road budget is reduced
 * by whatever those receipts could not cover.** The uncovered part is carried
 * (`owed`) and keeps taking the road line until it is retired. A commitment
 * *smaller* than a cycle's receipts therefore costs the roads nothing at all —
 * which is what makes the slack real rather than notional. See `ration`.
 *
 * ### What counts as a commitment, and what does not
 *
 * A commitment is a **decision**: a governor's levy on a season's revenue, or a
 * mercy build a mayor chose to buy. §8's thesis is that a decision above the
 * district lands as potholes below it, and `owed` is that sentence in arithmetic.
 *
 * The metered utility bill is not one, and `bill` gives the two reasons at length.
 * Neither is UBI, and the reason there is the money's direction rather than
 * anybody's choice: UBI is a transfer to the district's *own* households, and the
 * closed loop returns it as tax on what they spend. Measured over a season it comes
 * back at `2t/(1+t)` of household turnover, which at the shipped `TAX_RATE` of 0.25
 * is 40 cents in the dollar — and is why the district has always run a deficit
 * against itself. **That deficit is its money supply**, and it is not this rule's
 * business.
 *
 * ### What this makes true, which was not true before
 *
 * ADR-020 §7 Q43 asked for it and ADR-020 D129 named the option and deferred it
 * here: *"spend on a covenant, get potholes"*. A mercy build is a commitment to
 * somebody outside the district, so it competes with the road budget, so:
 *
 *   - in a **good** season — no levy outstanding — an $80 hedgerow is covered by one
 *     cycle's receipts outright and the roads never notice; twelve covenants cost a
 *     district four points of road quality;
 *   - in a **bad** one — a 22% tithe outstanding — the same twelve cost it
 *     fifty-eight, because they land behind three thousand dollars of levy;
 *   - and against a **grant**, which leaves credit (`levy`), they are free.
 *
 * Measured, two seasons, one seed: road quality 96, 38 and 96; mean quality of life
 * 0.913, 0.827 and 0.913.
 *
 * Which is `IMPLEMENTATION-GOVERNORS.md` §6's second exit criterion, word for
 * word: *"Mercy must be affordable in good times and expensive in bad ones, by
 * arithmetic alone."*
 *
 * ## What `owed` is not
 *
 * Not a second treasury. Every dollar here is deducted from the government pool
 * at the moment it is charged, exactly as before, by `EconomyManager`. `owed` is a
 * **rationing device**: it decides what the discretionary line gets and nothing
 * else. It creates no money, destroys none, and a world with this class deleted
 * would settle the same pool balance — with its roads in better repair.
 *
 * ## What this class cannot see
 *
 * A hash type, and nothing else at all. Not the grid, not the mains, not the
 * treasury, not a house. It is handed two lifetime totals and a lifetime tax
 * figure and hands back dollars; who drew the kilowatts and who pays the bill are
 * both somebody else's business. `tests/purity.test.ts` pins the import list.
 *
 * See `docs/economy.md`.
 */

import { Canonical } from '../hashState';

/**
 * The polity's prices, and the Charter's cap. World parameters, not state.
 *
 * §5 gives every flow *"a world-market price of last resort"* and §8 bills the
 * district *"at polity prices"*. There is no polity, so these are the prices of
 * last resort, and they are chosen against the district's own measured receipts:
 * a grown district collects about **$147 of tax a cycle** and draws about
 * **480 kW** and **122 m³** in the same cycle, so the two prices below put the
 * utility bill at roughly **a third of receipts** — about $51.
 *
 * A third is the calibration, and it is the only number here that was chosen rather
 * than measured. What it is chosen against is legibility rather than pressure — the
 * bill does not ration anything (`bill`), so its size decides how large a line it is
 * in a Chronicle and how fast it deepens a deficit, and nothing else. A third of
 * receipts puts it below the tax and above the road budget, which is where a
 * municipal utility bill belongs and is a figure a reader can sanity-check against
 * the two numbers beside it.
 */
export interface FiscalConfig {
  /** Dollars per kilowatt-cycle delivered. A four-kilowatt home: $0.32 a cycle. */
  POWER_PRICE: number;
  /** Dollars per cubic metre delivered. The same home's taps: $0.13 a cycle. */
  WATER_PRICE: number;
  /**
   * How far the tithe dial turns, either way.
   *
   * §8 says *"Charter-capped, e.g. 0–40%"* and gives the negative half to §9's
   * Long Summer, so the cap is symmetric: a governor may take 40% of a district's
   * tax revenue or send 40% of it back. The Charter that should own this number is
   * Phase 7's (`IMPLEMENTATION-GOVERNORS.md` §11); until then it is a world
   * parameter with the design's own example in it.
   */
  TITHE_CAP: number;
}

export const DEFAULT_FISCAL_CONFIG: FiscalConfig = {
  POWER_PRICE: 0.08,
  WATER_PRICE: 0.1,
  TITHE_CAP: 0.4,
};

/**
 * The account's complete state. See `FiscalManager.save()`.
 *
 * Three marks, one carried balance, one dial, and the ledger. The marks are what
 * makes every charge here a subtraction over a lifetime total the *other* manager
 * owns — the same idiom `seasonFlows` uses on the Chronicle, for the same reason:
 * a figure re-derived from the source cannot disagree with it.
 */
export interface FiscalSnapshot {
  titheRate: number;
  owed: number;
  kwMark: number;
  m3Mark: number;
  taxMark: number;
  powerBilled: number;
  waterBilled: number;
  tithePaid: number;
  grantsReceived: number;
  worksCommitted: number;
  infraUnpaid: number;
  billedCycles: number;
  constrainedCycles: number;
  levies: number;
}

/**
 * What a district may still spend on itself this cycle.
 *
 * The interface `EconomyManager` rations against, declared where it is *used*
 * rather than imported from here. The treasury has never known what a tithe is
 * and does not need to start: it knows what it received and what it would like to
 * spend, and something else decides how much of that is available.
 *
 * `FiscalManager` satisfies it structurally. `SurfaceSim` is what hands one to the
 * other, which is where every other wire between two managers in this package is
 * tied.
 */
export interface SpendingLimit {
  /**
   * How much of `want` this cycle's receipts can fund.
   *
   * Called once per settled budget cycle, with the receipts of the cycle just
   * closed. Mutating: outstanding commitments are retired against the receipts,
   * and a shortfall is recorded.
   */
  ration(receipts: number, want: number): number;
}

/**
 * The district's account with the polity above it.
 *
 * Owned by `SurfaceSim`, charged once per settled budget cycle and once per
 * season, and snapshotted inside the surface like every other manager here.
 */
export class FiscalManager implements SpendingLimit {
  private config: FiscalConfig;

  /**
   * The share of each season's tax revenue remitted upward. Negative is a grant.
   *
   * Zero until a governor says otherwise, and there is no governor: §8's
   * prosperity case has *"rational governors set tithes near zero as cheap
   * goodwill"*, so a district nobody has addressed sits at the bottom of the dial
   * rather than at some invented default. Which is also why every recorded run of
   * this district still has an empty fiscal block in it.
   */
  private titheRate = 0;

  /**
   * Dollars committed to somebody outside the district and not yet covered by
   * receipts.
   *
   * The only field here that is a *balance* rather than a total. See the header
   * for what it is and is not: it decides the discretionary line, it is not money,
   * and it never goes below zero — unspent receipts are not banked, because they
   * are already in the pool, which is the district's actual money.
   */
  private owed = 0;

  // The three marks. Each is the last value of a lifetime total this account has
  // already charged for, so a charge is `total - mark` and cannot double-bill
  // however often it is called.
  private kwMark = 0;
  private m3Mark = 0;
  private taxMark = 0;

  private powerBilled = 0;
  private waterBilled = 0;
  private tithePaid = 0;
  private grantsReceived = 0;
  private worksCommitted = 0;
  private infraUnpaid = 0;
  private billedCycles = 0;
  private constrainedCycles = 0;
  private levies = 0;

  constructor(config: Partial<FiscalConfig> = {}) {
    this.config = { ...DEFAULT_FISCAL_CONFIG, ...config };
  }

  getConfig(): FiscalConfig {
    return { ...this.config };
  }

  updateConfig(partial: Partial<FiscalConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  /**
   * Whether any money has ever passed through this account.
   *
   * True in a district nobody has billed, tithed or rationed — an untouched
   * meadow draws nothing, so it is charged nothing, so it has no fiscal state and
   * writes no block. The guard every peer in this package has, for the reason
   * `SeasonFacilities` gives: an empty account and no account are the same
   * account, and a hash that distinguished them would have re-recorded a golden
   * master to say that nothing happened.
   */
  get empty(): boolean {
    return this.billedCycles === 0 && this.levies === 0 &&
           this.owed === 0 && this.titheRate === 0;
  }

  /** The share of each season's tax revenue going upward. Negative is a grant. */
  getTitheRate(): number {
    return this.titheRate;
  }

  /**
   * Turn the extraction dial. Returns whether it moved.
   *
   * Refused outside the Charter's cap rather than clamped, and refused when it
   * names the rate the district is already on. Both refusals are `applied: false`
   * at the command layer, which is the standing a `PAVE` on wet concrete has: a
   * well-formed sentence about a world it does not fit.
   *
   * Clamping was the alternative and is worse for one reason. §16 publishes this
   * number; a governor who asked for 80% and got 40% would appear in the record as
   * a governor who asked for 40%, and the Chronicle would be quietly wrong about
   * the most consequential thing anybody above the district ever does.
   */
  setTitheRate(rate: number): boolean {
    if (!Number.isFinite(rate)) return false;
    if (Math.abs(rate) > this.config.TITHE_CAP) return false;
    if (rate === this.titheRate) return false;
    this.titheRate = rate;
    return true;
  }

  /** Dollars committed outside the district and not yet covered by receipts. */
  getOwed(): number {
    return this.owed;
  }

  /** The lifetime totals. Monotone sums, so a season's flows are a subtraction. */
  getLedger() {
    return {
      powerBilled: this.powerBilled,
      waterBilled: this.waterBilled,
      tithePaid: this.tithePaid,
      grantsReceived: this.grantsReceived,
      worksCommitted: this.worksCommitted,
      infraUnpaid: this.infraUnpaid,
      billedCycles: this.billedCycles,
      constrainedCycles: this.constrainedCycles,
      levies: this.levies,
    };
  }

  // --- The bills ------------------------------------------------------------

  /**
   * Bill one cycle's delivered flows. Returns the dollars due.
   *
   * Handed the two lifetime totals the meters keep, so the charge is what has
   * been delivered since the last bill. §8 says *"metered at polity prices"*, and
   * **delivered** is the load-bearing word: a district the grid shed pays for
   * nothing it did not get, which makes a brownout a discount and is the one place
   * in this build where a shortage is worth money to the district it happens to.
   *
   * The discount does not compensate. A home that spent the cycle dark saves its
   * own $0.48 and loses a cycle of light, and §10's quality-of-life score counts
   * the second (ADR-018). A mayor cannot balance a budget by switching the town
   * off, because the households leave first.
   *
   * **The bill is not a commitment and does not reach `owed`.** It is paid the way
   * UBI is paid — out of the pool, by borrowing if it must — and it therefore
   * changes nothing about the road budget. Which makes it the fifth ledger in this
   * build to ship ahead of the mechanic that reads it, and the reason is worth
   * stating rather than glossing, because the alternative was built first and
   * measured:
   *
   *   - **A commitment is a decision.** A tithe is a governor's claim on a season's
   *     revenue; a covenant is a purchase a mayor chose. §8's thesis is that a
   *     decision above the district lands as potholes below it, and `owed` is that
   *     sentence in arithmetic. Metered consumption already delivered is not a
   *     decision: the kilowatts arrived before the invoice existed and nobody could
   *     have declined them.
   *   - **And it is not cadence-safe.** With the bill in the rule, a district's road
   *     budget is short for its first four cycles at the shipped ten-second cycle
   *     and for eighty at a one-second one — because a town's receipts arrive on
   *     sim-time while its streetlights are billed per cycle, so the ramp is longer
   *     in cycles when a cycle is shorter. The pressure that produced was a function
   *     of the budget period rather than of anybody's decision, which is the
   *     opposite of what §8 asks for.
   *
   * What the bill does today is make the deficit larger, and the deficit still costs
   * nothing. What will read it is §8's bond yield — *"a published deterministic
   * function of debt-to-revenue"* — which needs a polity to issue bonds and is
   * Phase 3's.
   */
  bill(deliveredKwCycles: number, deliveredM3Cycles: number): number {
    const kw = Math.max(0, deliveredKwCycles - this.kwMark);
    const m3 = Math.max(0, deliveredM3Cycles - this.m3Mark);
    this.kwMark = deliveredKwCycles;
    this.m3Mark = deliveredM3Cycles;

    const power = kw * this.config.POWER_PRICE;
    const water = m3 * this.config.WATER_PRICE;
    const due = power + water;
    this.powerBilled += power;
    this.waterBilled += water;
    // Counted only when there was something to bill for, so an untouched meadow —
    // no lit cells, no buildings, nothing drawn — keeps an empty account and writes
    // no block. `empty` is what the omission everywhere else hangs off, and a
    // counter that ticked for a district with nothing on it would have made the
    // district with nothing on it the one district that could not be omitted.
    if (due > 0) this.billedCycles++;

    return due;
  }

  // --- The tithe ------------------------------------------------------------

  /**
   * Levy the season's tithe. Returns the dollars due — negative for a grant.
   *
   * Handed the treasury's lifetime tax figure, so the base is the tax collected
   * since the last levy, which is exactly §8's *"% of district tax revenue"* over
   * exactly one season. Nothing else is in the base: not the estate's rent, not
   * the wages, not the dividends. A tithe on rent would be a polity taxing its own
   * spending, and a tithe on turnover would be a second tax rate with the
   * governor's hand on it.
   *
   * Called from the `fiscal` stage of the season settlement, which until now had
   * nothing in it. That makes this the first thing in the game that **moves money
   * at a season boundary**, and `SeasonManager`'s header used to say settlement
   * decides nothing at all. It says something narrower now, and the narrowing is
   * this one line.
   */
  levy(taxCollectedLifetime: number): number {
    const base = Math.max(0, taxCollectedLifetime - this.taxMark);
    this.taxMark = taxCollectedLifetime;

    const due = base * this.titheRate;
    // Counted only when something moved, like `billedCycles` and for the same
    // reason: `empty` is what every omission in this class hangs off, and a
    // counter that ticked at every season boundary would make a district nobody
    // has ever addressed the one district whose account cannot be omitted.
    if (due !== 0) this.levies++;
    if (due > 0) {
      this.tithePaid += due;
      this.owed += due;
    } else if (due < 0) {
      const grant = -due;
      this.grantsReceived += grant;
      // A grant leaves **credit**: `owed` goes negative and stays there until
      // something is committed against it. That is the one direction this balance is
      // allowed to run below zero, and it is what makes §9's Long Summer work —
      // *"District B's mayor, flush, finally builds the two things that have been
      // sitting unaffordable in the build menu"*. A covenant bought against credit
      // costs the road budget nothing, because somebody already paid for it.
      //
      // Unspent *receipts* are never banked this way (see `ration`). Receipts are
      // already in the pool, which is the district's actual money; a grant is a
      // transfer with no other purpose, and pretending it had been spent the moment
      // it arrived would be losing it.
      this.owed -= grant;
    }
    return due;
  }

  // --- The district's own works ---------------------------------------------

  /**
   * Commit dollars the district has spent on something outside itself.
   *
   * §10's mercy builds, and today only those: a covenant, a hedgerow, a way under
   * a road. The money has already left the pool (`EconomyManager.payForWorks`);
   * this is the claim it makes on the road budget until receipts have covered it.
   *
   * Which is the mechanism ADR-020 D129 considered, rejected as *"invented"*
   * because it would have rewritten the treasury's constraint model under cover of
   * adding a build item, and named §8's job. This is §8. A build here is not
   * treated differently from a power bill or a levy, and it is the third of three
   * lines in one rule rather than a rule about mercy.
   */
  commitWorks(amount: number): void {
    if (!(amount > 0)) return;
    this.worksCommitted += amount;
    this.owed += amount;
  }

  // --- The rule -------------------------------------------------------------

  /**
   * How much of `want` this cycle's receipts can fund.
   *
   * ```
   * arrears = max(0, owed − receipts)   ← commitments receipts could not cover
   * granted = max(0, want − arrears)    ← the road budget, less the arrears
   * owed    = arrears                   ← carried to the next cycle
   * ```
   *
   * Three lines, and every quantity in them is measured: receipts are the tax and
   * rent of the cycle just closed, `owed` is a sum over levies and builds, and
   * `want` is the mayor's own slider. Nothing here is a rate, a curve or a
   * threshold, which is the point — §8's pressure is meant to be arithmetic a reader
   * can walk back to dollars settled, not a difficulty setting.
   *
   * The subtraction is `want − arrears` and not `min(want, receipts − owed)`, and
   * the difference is the whole behaviour of the rule. A district that commits less
   * than its receipts is **not rationed at all**: an $80 hedgerow against $190 of
   * receipts leaves the road budget untouched, because the receipts covered it. Only
   * a commitment larger than a cycle's receipts reaches the roads, and it reaches
   * them by exactly what it overshot by.
   *
   * Which is what makes "affordable in good times" arithmetic rather than a claim:
   * the slack *is* the receipts, so the same covenant costs a prosperous district
   * nothing and a tithed one a month of maintenance.
   *
   * A district running the shipped defaults with no governor above it is never
   * constrained: it commits nothing, so `arrears` is zero, so the road budget is
   * whatever the mayor set. What constrains it is a levy or its own generosity —
   * which is where §8 wants the pressure to come from.
   */
  ration(receipts: number, want: number): number {
    const income = Math.max(0, receipts);
    const arrears = Math.max(0, this.owed - income);
    const asked = Math.max(0, want);
    const granted = Math.max(0, asked - arrears);

    // Credit is left alone: receipts retire debt and are not banked as more of
    // it. A grant is drawn down by what the district commits against it, not by
    // the arrival of a cycle's tax.
    if (this.owed > 0) this.owed = arrears;

    if (granted < asked) {
      this.infraUnpaid += asked - granted;
      this.constrainedCycles++;
    }
    return granted;
  }

  // --- State ---------------------------------------------------------------

  save(): FiscalSnapshot {
    return {
      titheRate: this.titheRate,
      owed: this.owed,
      kwMark: this.kwMark,
      m3Mark: this.m3Mark,
      taxMark: this.taxMark,
      powerBilled: this.powerBilled,
      waterBilled: this.waterBilled,
      tithePaid: this.tithePaid,
      grantsReceived: this.grantsReceived,
      worksCommitted: this.worksCommitted,
      infraUnpaid: this.infraUnpaid,
      billedCycles: this.billedCycles,
      constrainedCycles: this.constrainedCycles,
      levies: this.levies,
    };
  }

  /**
   * Restore from `save()`.
   *
   * The three marks matter more here than anywhere else in the package. A district
   * restored with `kwMark` at zero bills itself for every kilowatt it has ever
   * drawn, in one cycle — and with `taxMark` at zero it remits a tithe on the whole
   * history of its own economy. Both would be silent: the pool is one number and
   * nothing announces where it went.
   */
  load(state: FiscalSnapshot): void {
    this.titheRate = state.titheRate;
    this.owed = state.owed;
    this.kwMark = state.kwMark;
    this.m3Mark = state.m3Mark;
    this.taxMark = state.taxMark;
    this.powerBilled = state.powerBilled;
    this.waterBilled = state.waterBilled;
    this.tithePaid = state.tithePaid;
    this.grantsReceived = state.grantsReceived;
    this.worksCommitted = state.worksCommitted;
    this.infraUnpaid = state.infraUnpaid;
    this.billedCycles = state.billedCycles;
    this.constrainedCycles = state.constrainedCycles;
    this.levies = state.levies;
  }

  /**
   * Order-stable view of the account, for hashing — and nothing while it is empty.
   *
   * The dial and the carried balance are in it as well as the totals, and both
   * have to be: two districts that agree on every dollar ever billed and disagree
   * about the tithe rate have not diverged *yet*, and will at the next season
   * boundary. The marks are in it for the same reason — a district that has billed
   * for its kilowatts and one that has not are two different bills waiting to
   * happen.
   */
  canonicalState(): Canonical | undefined {
    if (this.empty) return undefined;
    return {
      titheRate: this.titheRate,
      owed: this.owed,
      kwMark: this.kwMark,
      m3Mark: this.m3Mark,
      taxMark: this.taxMark,
      powerBilled: this.powerBilled,
      waterBilled: this.waterBilled,
      tithePaid: this.tithePaid,
      grantsReceived: this.grantsReceived,
      worksCommitted: this.worksCommitted,
      infraUnpaid: this.infraUnpaid,
      billedCycles: this.billedCycles,
      constrainedCycles: this.constrainedCycles,
      levies: this.levies,
    };
  }
}
